import { useCallback, useEffect, useRef, useState } from 'react';

import { JonumMissingUserInfo } from '#src/__generated__/globalTypes.ts';
import { JONUM_FLOW } from '#src/components/fiatWallet/CreateFiatWallet/HostedIdentityVerification/returnUrl.ts';
import { KycFlow } from '#src/components/kyc/KycFlow/index.tsx';

import { AccountVerificationContext } from './AccountVerification/AccountVerificationContext.tsx';
import { IdentityVerificationCTA } from './AccountVerification/IdentityVerificationCTA/index.tsx';
import { AccountVerification } from './AccountVerification/index.tsx';
import { AccountVerificationStep } from './AccountVerification/type.ts';
import { JonumVerificationInProgress } from './JonumVerificationInProgress/index.tsx';
import {
  useJonumVerificationGuard,
  withoutKycInfo,
} from './useJonumVerificationGuard.ts';

type Props = {
  onDone: () => void;
};

export const JonumKycPromptView = ({ onDone }: Props) => {
  const {
    infoRequired,
    mangopayKycUnderReview,
    mangopayKycValidated,
    missingInfo,
    needsClassification,
    onStepComplete,
    shouldShow,
  } = useJonumVerificationGuard();
  const [collectsInfo] = useState(infoRequired);
  const [reclassifies] = useState(shouldShow && needsClassification);
  const classifiedMissingInfo = useRef<JonumMissingUserInfo[] | null>(null);
  const closed = useRef(false);
  const [infoStepsCompleted, setInfoStepsCompleted] = useState(false);
  const [showKycFlow, setShowKycFlow] = useState(false);
  const infoStepsPending =
    (reclassifies || collectsInfo) && !infoStepsCompleted;
  const kycAlreadyValidated =
    !infoStepsPending && !showKycFlow && mangopayKycValidated;

  const closeOnce = useCallback(() => {
    if (closed.current) return;

    closed.current = true;
    onDone();
  }, [onDone]);

  useEffect(() => {
    if (kycAlreadyValidated) {
      closeOnce();
    }
  }, [kycAlreadyValidated, closeOnce]);

  if (infoStepsPending) {
    return (
      <AccountVerification
        showIntro={reclassifies}
        missingInfo={withoutKycInfo(missingInfo)}
        onStepComplete={async (step, data) => {
          const result = await onStepComplete(step, data);
          if (step !== AccountVerificationStep.INTRO || !result) return result;

          classifiedMissingInfo.current = result;
          return withoutKycInfo(result);
        }}
        onComplete={() => {
          const kycInfoMissing = (
            classifiedMissingInfo.current ?? missingInfo
          ).includes(JonumMissingUserInfo.KYC);
          const kycUploadOwed =
            kycInfoMissing && !mangopayKycValidated && !mangopayKycUnderReview;

          if (kycUploadOwed) {
            setInfoStepsCompleted(true);
          } else {
            closeOnce();
          }
        }}
        onSkip={closeOnce}
        onBack={closeOnce}
      />
    );
  }

  if (showKycFlow) {
    return (
      <KycFlow
        onDone={closeOnce}
        hostedVerificationFlow={JONUM_FLOW}
        renderVerificationInProgress={({ onDone: onJonumDone }) => (
          <JonumVerificationInProgress onDone={onJonumDone} />
        )}
      />
    );
  }

  if (kycAlreadyValidated) {
    return null;
  }

  return (
    <AccountVerificationContext
      value={{ onSkip: closeOnce, updateData: () => {}, loading: false }}
    >
      <IdentityVerificationCTA onNext={() => setShowKycFlow(true)} />
    </AccountVerificationContext>
  );
};
